import { getEBankruptcyTypeList } from '@if/core/types'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import DeleteIcon from '@mui/icons-material/Delete'
import { Divider, Grid, IconButton } from '@mui/material'
import { useMemo } from 'react'
import { Control, FieldError, FieldErrorsImpl, Merge, UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextField, SelectComponent } from '../../../components'
import { AdditionnalBankruptcies, ApplicantBankruptcies, SelectValueListItem } from '../../../data/types'

interface Props {
  index: number
  isSyndicReported?: boolean
  register: UseFormRegister<ApplicantBankruptcies>
  error: Merge<FieldError, (Merge<FieldError, FieldErrorsImpl<AdditionnalBankruptcies>> | undefined)[]> | undefined
  control: Control<ApplicantBankruptcies>
  fieldName: 'additionnalBankruptcies'
  onDeleteClicked: () => void
}

const Bankruptcy = ({ index, register, error, control, isSyndicReported, fieldName, onDeleteClicked }: Props) => {
  const { t } = useTranslation()

  const bankruptcyTypeOptions = useMemo<SelectValueListItem[]>(
    () =>
      getEBankruptcyTypeList().map((item) => ({ label: t(`EBankruptcyType.${item}`, { ns: 'enum' }), value: item })),
    [t],
  )

  return (
    <>
      <Grid container spacing={2} sx={{ mt: 2 }} size={12}>
        <Grid
          size={{
            xs: 12,
            md: 2,
          }}
        >
          <ControlledStringDateOnlyPicker
            name={`${fieldName}.${index}.filedD`}
            control={control}
            label="Date"
            disableFuture
            {...{ disabled: !isSyndicReported }}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 1.5,
          }}
        >
          <InputTextField
            error={error ? error[index]?.liabilityAmount : undefined}
            label={t('browseCreditApplications.dataGridColumnHeaders.amount')}
            {...register(`${fieldName}.${index}.liabilityAmount`)}
            disabled={!isSyndicReported}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={bankruptcyTypeOptions}
            label={t('browseCreditApplications.dataGridColumnHeaders.bankruptcyType')}
            {...(fieldName && register(`${fieldName}.${index}.intentCode`))}
            error={error ? error[index]?.intentCode : undefined}
            disabled={!isSyndicReported}
          />
        </Grid>
        <Grid
          size={{
            xs: 11,
            md: 3,
          }}
        >
          <InputTextField
            error={error ? error[index]?.trusteeName : undefined}
            label={t('browseCreditApplications.dataGridColumnHeaders.TrusteeName')}
            {...(fieldName && register(`${fieldName}.${index}.trusteeName`))}
            disabled={!isSyndicReported}
          />
        </Grid>
        <Grid
          size={{
            xs: 11,
            md: 2,
          }}
        >
          <InputTextField
            error={error ? error[index]?.caseNumber : undefined}
            label={t('browseCreditApplications.dataGridColumnHeaders.CaseNumber')}
            {...(fieldName && register(`${fieldName}.${index}.caseNumber`))}
            disabled={!isSyndicReported}
          />
        </Grid>
        {isSyndicReported && (
          <Grid>
            <IconButton sx={{ mb: 3 }} aria-label="delete" onClick={onDeleteClicked}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <Divider />
    </>
  )
}

export default Bankruptcy
